import { z } from 'zod'
import { createEnv } from '@t3-oss/env-nextjs'

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    REVALIDATE_SECRET: z.string(),
    DRAGONFLY_URL: z.string(),
    FLAGS_SECRET: z.string(),
    DANGEROUS_PROD_ONLY_DATABASE_URL: z.string().url(),
    CANARY_ONLY_DATABASE_URL: z.string().url(),
    DATABASE_URL: z.string().url(),
    NODE_ENV: z.enum(['development', 'test', 'production']),
    GITHUB_CLIENT_ID: z.string(),
    GITHUB_CLIENT_SECRET: z.string(),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    R2_BUCKET_NAME: z.string(),
    CLOUDFLARE_ACCOUNT_ID: z.string(),
    CLOUDFLARE_ANALYTICS_ENGINE_API_TOKEN: z.string(),
    R2_ACCESS_KEY_ID: z.string(),
    R2_SECRET_ACCESS_KEY: z.string(),
    CLOUDFLARE_IMAGES_AND_VIDEO_API_TOKEN: z.string(),
    CLOUDFLARE_WORKERS_API_TOKEN: z.string(),
    REPLICATE_API_TOKEN: z.string(),
    NOTION_WAITLIST_BOT_SECRET: z.string(),
    OPENAI_API_KEY: z.string(),
    UPSTASH_REDIS_REST_URL: z.string(),
    UPSTASH_REDIS_REST_TOKEN: z.string(),
    GRAPHQL_UPSTASH_REDIS_REST_URL: z.string(),
    GRAPHQL_UPSTASH_REDIS_REST_TOKEN: z.string(),
    UPSTASH_REDIS_REST_READ_ONLY_TOKEN: z.string(),
    RESEND_API_KEY: z.string(),
    SUPABASE_WEBHOOK_SECRET: z.string(),
    CLERK_EMAIL_WEBHOOK_SIGNING_SECRET: z.string(),
    GITHUB_FEEDBACK_BOT_PRIVATE_KEY: z.string(),
    GITHUB_FEEDBACK_BOT_APP_ID: z.string(),
    GRAPHQL_SUPERADMIN_READ_TOKEN: z.string(),
    CLERK_SECRET_KEY: z.string(),
    NPM_TOKEN: z.string(),
    INNGEST_EVENT_KEY: z.string(),
    INNGEST_REST_URL: z.string(),
    INNGEST_SIGNING_KEY: z.string(),
    PUSHER_SECRET: z.string(),
    TINYBIRD_API_KEY: z.string(),
    UPSTASH_KAFKA_USERNAME: z.string(),
    UPSTASH_KAFKA_PASSWORD: z.string(),
    AUTOBLOCKS_INGESTION_KEY: z.string(),
    AUTOBLOCKS_API_KEY: z.string(),
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    SVIX_TOKEN: z.string(),
    MUTATOR_SERVER_AUTH_SECRET: z.string(),
    VERCEL_INTEGRATION_CLIENT_SECRET: z.string(),
    BASEHUB_WEBHOOK_SECRET: z.string(),
    GROQ_TOKEN: z.string(),
    TYPESENSE_API_KEY: z.string(),
    TYPESENSE_DOMAIN: z.string(),
    TYPESENSE_SEARCH_ONLY_API_KEY: z.string(),
    API_SERVER_SECRET: z.string(),
    CLICKHOUSE_URL: z.string(),
    CLICKHOUSE_USER: z.string(),
    CLICKHOUSE_PASSWORD: z.string(),
    QSTASH_TOKEN: z.string(),
    QSTASH_URL: z.string(),
    BROWSER_SERVER_SECRET: z.string(),
    BASEHUB_ICON_LIBRARY_TOKEN: z.string(),
    VERCEL_AUTOMATION_BYPASS_SECRET: z.string(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_SITE_URL: z.string(),
    NEXT_PUBLIC_REPLICACHE_LOG: z
      .union([z.string(), z.boolean()])
      .transform((s) => s === true || s === 'true'),
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID: z.string(),
    NEXT_PUBLIC_PUSHER_APP_ID: z.string(),
    NEXT_PUBLIC_PUSHER_KEY: z.string(),
    NEXT_PUBLIC_PUSHER_CLUSTER: z.string(),
    NEXT_PUBLIC_UPSTASH_KAFKA_URL: z.string(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string(),
    NEXT_PUBLIC_API_URL: z.string(),
    NEXT_PUBLIC_HYPERDX_INNGESTION_KEY: z.string(),
    NEXT_PUBLIC_VERCEL_INTEGRATION_CLIENT_ID: z.string(),
    NEXT_PUBLIC_DISABLE_ANNOUNCEMENTS: z
      .union([z.string(), z.boolean()])
      .optional()
      .default(false)
      .transform((s) => s === true || s === 'true'),
    NEXT_PUBLIC_GOOGLE_FONTS_API_KEY: z.string(),
    NEXT_PUBLIC_BASEHUB_TOKEN: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    // server
    REVALIDATE_SECRET: process.env.REVALIDATE_SECRET,
    DRAGONFLY_URL: process.env.DRAGONFLY_URL,
    VERCEL_AUTOMATION_BYPASS_SECRET:
      process.env.VERCEL_AUTOMATION_BYPASS_SECRET ??
      (process.env.NODE_ENV === 'development' ? 'placeholder' : undefined),
    FLAGS_SECRET: process.env.FLAGS_SECRET,
    BASEHUB_WEBHOOK_SECRET: process.env.BASEHUB_WEBHOOK_SECRET,
    GROQ_TOKEN: process.env.GROQ_TOKEN,
    DANGEROUS_PROD_ONLY_DATABASE_URL:
      process.env.DANGEROUS_PROD_ONLY_DATABASE_URL,
    DATABASE_URL: process.env.DATABASE_URL,
    CANARY_ONLY_DATABASE_URL: process.env.CANARY_ONLY_DATABASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    GITHUB_CLIENT_ID: process.env.GITHUB_CLIENT_ID,
    GITHUB_CLIENT_SECRET: process.env.GITHUB_CLIENT_SECRET,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    R2_BUCKET_NAME: process.env.R2_BUCKET_NAME,
    CLOUDFLARE_ACCOUNT_ID: process.env.CLOUDFLARE_ACCOUNT_ID,
    CLOUDFLARE_ANALYTICS_ENGINE_API_TOKEN:
      process.env.CLOUDFLARE_ANALYTICS_ENGINE_API_TOKEN,
    R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID,
    R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY,
    CLOUDFLARE_IMAGES_AND_VIDEO_API_TOKEN:
      process.env.CLOUDFLARE_IMAGES_AND_VIDEO_API_TOKEN,
    CLOUDFLARE_WORKERS_API_TOKEN: process.env.CLOUDFLARE_WORKERS_API_TOKEN,
    REPLICATE_API_TOKEN: process.env.REPLICATE_API_TOKEN,
    NOTION_WAITLIST_BOT_SECRET: process.env.NOTION_WAITLIST_BOT_SECRET,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    GRAPHQL_UPSTASH_REDIS_REST_URL: process.env.GRAPHQL_UPSTASH_REDIS_REST_URL,
    GRAPHQL_UPSTASH_REDIS_REST_TOKEN:
      process.env.GRAPHQL_UPSTASH_REDIS_REST_TOKEN,
    UPSTASH_REDIS_REST_READ_ONLY_TOKEN:
      process.env.UPSTASH_REDIS_REST_READ_ONLY_TOKEN,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    SUPABASE_WEBHOOK_SECRET: process.env.SUPABASE_WEBHOOK_SECRET,
    CLERK_EMAIL_WEBHOOK_SIGNING_SECRET:
      process.env.CLERK_EMAIL_WEBHOOK_SIGNING_SECRET,
    GITHUB_FEEDBACK_BOT_PRIVATE_KEY:
      process.env.GITHUB_FEEDBACK_BOT_PRIVATE_KEY,
    GITHUB_FEEDBACK_BOT_APP_ID: process.env.GITHUB_FEEDBACK_BOT_APP_ID,
    GRAPHQL_SUPERADMIN_READ_TOKEN: process.env.GRAPHQL_SUPERADMIN_READ_TOKEN,
    CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
    NPM_TOKEN: process.env.NPM_TOKEN,
    INNGEST_EVENT_KEY: process.env.INNGEST_EVENT_KEY,
    INNGEST_REST_URL: process.env.INNGEST_REST_URL,
    INNGEST_SIGNING_KEY: process.env.INNGEST_SIGNING_KEY,
    PUSHER_SECRET: process.env.PUSHER_SECRET,
    TINYBIRD_API_KEY: process.env.TINYBIRD_API_KEY,
    UPSTASH_KAFKA_USERNAME: process.env.UPSTASH_KAFKA_USERNAME,
    UPSTASH_KAFKA_PASSWORD: process.env.UPSTASH_KAFKA_PASSWORD,
    AUTOBLOCKS_INGESTION_KEY: process.env.AUTOBLOCKS_INGESTION_KEY,
    AUTOBLOCKS_API_KEY: process.env.AUTOBLOCKS_API_KEY,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    SVIX_TOKEN: process.env.SVIX_TOKEN,
    MUTATOR_SERVER_AUTH_SECRET: process.env.MUTATOR_SERVER_AUTH_SECRET,
    TYPESENSE_API_KEY: process.env.TYPESENSE_API_KEY,
    TYPESENSE_DOMAIN: process.env.TYPESENSE_DOMAIN,
    TYPESENSE_SEARCH_ONLY_API_KEY: process.env.TYPESENSE_SEARCH_ONLY_API_KEY,
    API_SERVER_SECRET: process.env.API_SERVER_SECRET,
    VERCEL_INTEGRATION_CLIENT_SECRET:
      process.env.VERCEL_INTEGRATION_CLIENT_SECRET,
    QSTASH_TOKEN: process.env.QSTASH_TOKEN,
    QSTASH_URL: process.env.QSTASH_URL,
    BROWSER_SERVER_SECRET: process.env.BROWSER_SERVER_SECRET,
    BASEHUB_ICON_LIBRARY_TOKEN: process.env.BASEHUB_ICON_LIBRARY_TOKEN,
    // client
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_REPLICACHE_LOG:
      process.env.NEXT_PUBLIC_REPLICACHE_LOG === 'true',
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID:
      process.env.NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID,
    NEXT_PUBLIC_PUSHER_APP_ID: process.env.NEXT_PUBLIC_PUSHER_APP_ID,
    NEXT_PUBLIC_PUSHER_KEY: process.env.NEXT_PUBLIC_PUSHER_KEY,
    NEXT_PUBLIC_PUSHER_CLUSTER: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    NEXT_PUBLIC_UPSTASH_KAFKA_URL: process.env.NEXT_PUBLIC_UPSTASH_KAFKA_URL,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_HYPERDX_INNGESTION_KEY:
      process.env.NEXT_PUBLIC_HYPERDX_INNGESTION_KEY,
    NEXT_PUBLIC_VERCEL_INTEGRATION_CLIENT_ID:
      process.env.NEXT_PUBLIC_VERCEL_INTEGRATION_CLIENT_ID,
    NEXT_PUBLIC_GOOGLE_FONTS_API_KEY:
      process.env.NEXT_PUBLIC_GOOGLE_FONTS_API_KEY,
    CLICKHOUSE_URL: process.env.CLICKHOUSE_URL,
    CLICKHOUSE_USER: process.env.CLICKHOUSE_USER,
    CLICKHOUSE_PASSWORD: process.env.CLICKHOUSE_PASSWORD,
    NEXT_PUBLIC_BASEHUB_TOKEN: process.env.NEXT_PUBLIC_BASEHUB_TOKEN,
  },
})
